import React, { useState } from 'react';
import { useCartStore } from '../store/cartStore';
import { formatCurrency } from '../utils/helpers';
import BxgyAddYModal from './BxgyAddYModal';
import { useBxgyOffers, BxgyUnlockedCards, BxgyWarningPopup, addYLabel } from './BxgyOffers';

function Cart() {
  const { cart, updateQuantity, removeFromCart, clearCart, applyDiscount, removeDiscount } = useCartStore();
  const [discountCode, setDiscountCode] = useState('');
  const [, setShowDiscountInput] = useState(false);
  const [isApplyingDiscount, setIsApplyingDiscount] = useState(false);
  const [discountToast, setDiscountToast] = useState(null);
  // BXGY: this cart panel is the PRIMARY instance (always mounted on the POS page) — it auto-applies
  // offers whose Y is already in the cart and raises the "no saving" warning. See BxgyOffers.js.
  const { offers: bxgyOffers, autoErrors, dismissed, warning, setWarning } = useBxgyOffers({ primary: true });
  const [addYFor, setAddYFor] = useState(null); // { code, needQty } while the picker is open
  const offerFor = (code) => bxgyOffers.find(o => o.code === code && o.applied);
  const applyOfferByHand = async (o) => {
    const r = await applyDiscount(o.code, localStorage.getItem('selectedLocation'));
    setDiscountToast(r.success ? { type: 'success', message: r.autoAdded ? r.message : `${o.code} applied` } : { type: 'error', message: r.error || 'Could not apply' });
    setTimeout(() => setDiscountToast(null), 3000);
  };
  const addYButton = (o) => (
    <button onClick={() => setAddYFor({ code: o.code, needQty: o.needQty })}
      style={{ marginTop: '6px', padding: '5px 10px', background: '#f59e0b', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '12px', fontWeight: '700', cursor: 'pointer' }}>
      ＋ {addYLabel(o)}
    </button>
  );

  const handleQuantityChange = async (variantId, newQuantity, itemIndex) => {
    if (newQuantity < 1) {
      if (window.confirm('Remove this item from cart?')) {
        await removeFromCart(variantId, itemIndex);
      }
      return;
    }
    await updateQuantity(variantId, newQuantity, itemIndex);
  };

  const handleApplyDiscount = async () => { // eslint-disable-line no-unused-vars
    if (!discountCode.trim() || isApplyingDiscount) return;
    setIsApplyingDiscount(true);

    const result = await applyDiscount(discountCode, localStorage.getItem('selectedLocation'));
    if (result.success) {
      setDiscountCode('');
      setShowDiscountInput(false);
      setDiscountToast({ type: 'success', message: result.message || 'Discount code applied' });
    } else {
      setDiscountToast({ type: 'error', message: result.error || 'Invalid discount code' });
    }
    setIsApplyingDiscount(false);
    setTimeout(() => setDiscountToast(null), 3000);
  };

  return (
    <div style={{ padding: '20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h2 style={{ fontSize: '18px', fontWeight: '700', color: '#3d2b1f', margin: 0 }}>
          Cart
        </h2>
        {cart.items.length > 0 && (
          <button
            onClick={() => { if (window.confirm('Clear all items from cart?')) clearCart(); }}
            style={{ padding: '4px 10px', fontSize: '11px', fontWeight: '600', color: '#dc2626', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '10px', cursor: 'pointer' }}
          >
            Clear Cart
          </button>
        )}
      </div>

      {cart.items.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>🛒</div>
          <div>Cart is empty</div>
          <div style={{ fontSize: '14px', marginTop: '8px' }}>
            Add products to get started
          </div>
        </div>
      ) : (
        <>
          {/* Cart Items — grouped by location */}
          <div style={{ marginBottom: '20px' }}>
            {(() => {
              // Group items by locationId
              const groups = {};
              cart.items.forEach((item, idx) => {
                const locKey = item.locationId || 'unknown';
                if (!groups[locKey]) {
                  groups[locKey] = { locationName: item.locationName || 'Unknown Location', locationType: item.locationType || 'store', items: [] };
                }
                groups[locKey].items.push({ ...item, _idx: idx });
              });
              const groupKeys = Object.keys(groups);
              const multiLocation = groupKeys.length > 1;

              return groupKeys.map(locKey => (
                <div key={locKey}>
                  {multiLocation && (
                    <div style={{ fontSize: '11px', fontWeight: '700', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px', padding: '6px 0 4px', marginTop: '4px', borderBottom: '1px solid #e5e7eb', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: groups[locKey].locationType === 'warehouse' ? '#f59e0b' : '#22c55e', display: 'inline-block' }}></span>
                      {groups[locKey].locationName}
                      <span style={{ fontSize: '10px', fontWeight: '500', color: '#9ca3af' }}>({groups[locKey].locationType})</span>
                    </div>
                  )}
                  {groups[locKey].items.map(item => (
              <div
                key={`${item.variantId}-${item._idx}`}
                style={{
                  display: 'flex',
                  gap: '12px',
                  padding: '12px',
                  background: '#f5f0eb',
                  borderRadius: '10px',
                  marginBottom: '12px',
                  overflow: 'hidden'
                }}
              >
                {item.image && (
                  <img
                    src={item.image}
                    alt={item.title}
                    style={{
                      width: '60px',
                      height: '60px',
                      objectFit: 'cover',
                      borderRadius: '10px'
                    }}
                  />
                )}
                
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '13px', fontWeight: '600', color: '#3d2b1f', marginBottom: '4px', wordBreak: 'break-word' }}>
                    {item.title}
                  </div>
                  {item.customAttributes && item.customAttributes.length > 0 && (
                    <div style={{ fontSize: '11px', color: '#c88346', marginBottom: '4px', display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                      {item.customAttributes.map((attr, i) => (
                        <span key={i} style={{ background: '#fef3e2', padding: '2px 6px', borderRadius: '4px' }}>
                          {attr.key}: {attr.value}
                        </span>
                      ))}
                    </div>
                  )}
                  <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#c88346', display: 'flex', alignItems: 'baseline', gap: '6px' }}>
                    {formatCurrency(item.price)}
                    {item.compareAtPrice && parseFloat(item.compareAtPrice) > parseFloat(item.price) && (
                      <span style={{ fontSize: '11px', fontWeight: '400', color: '#a69889', textDecoration: 'line-through' }}>
                        {formatCurrency(item.compareAtPrice)}
                      </span>
                    )}
                    {item.personalizationFee > 0 && (
                      <span style={{ fontSize: '11px', fontWeight: '600', color: '#27ae60', marginLeft: '2px' }}>
                        +{formatCurrency(item.personalizationFee)} personalization
                      </span>
                    )}
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '8px' }}>
                    <button
                      onClick={() => handleQuantityChange(item.variantId, item.quantity - 1, item._idx)}
                      style={{
                        width: '28px',
                        height: '28px',
                        background: '#ffffff',
                        border: '1px solid #d1d5db',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '16px',
                        fontWeight: 'bold'
                      }}
                    >
                      -
                    </button>
                    <span style={{ fontSize: '14px', fontWeight: '600', minWidth: '20px', textAlign: 'center' }}>
                      {item.quantity}
                    </span>
                    {(() => {
                      const totalVariantQty = cart.items
                        .filter(i => i.variantId === item.variantId && String(i.locationId || '') === String(item.locationId || ''))
                        .reduce((sum, i) => sum + i.quantity, 0);
                      // Enforce stock limit when inventory > 0 (even for 'continue' policy)
                      // Only allow unlimited qty when inventory is 0 and policy is 'continue' (MTO) AND not a store location
                      const isMtoZeroStock = (item.locationType || 'store') !== 'store' && item.inventoryPolicy === 'continue' && (item.inventoryQuantity || 0) <= 0;
                      const atMax = !isMtoZeroStock && item.inventoryQuantity > 0 && totalVariantQty >= item.inventoryQuantity;
                      return (
                        <button
                          onClick={() => !atMax && handleQuantityChange(item.variantId, item.quantity + 1, item._idx)}
                          title={atMax ? `Max ${item.inventoryQuantity} in stock` : ''}
                          style={{
                            width: '28px',
                            height: '28px',
                            background: atMax ? '#f3f4f6' : 'white',
                            border: '1px solid #d1d5db',
                            borderRadius: '4px',
                            cursor: atMax ? 'not-allowed' : 'pointer',
                            fontSize: '16px',
                            fontWeight: 'bold',
                            opacity: atMax ? 0.4 : 1
                          }}
                        >
                          +
                        </button>
                      );
                    })()}
                    <button
                      onClick={() => removeFromCart(item.variantId, item._idx)}
                      style={{
                        marginLeft: 'auto',
                        padding: '4px 12px',
                        background: '#fee2e2',
                        color: '#dc2626',
                        border: 'none',
                        borderRadius: '4px',
                        fontSize: '12px',
                        fontWeight: '600',
                        cursor: 'pointer'
                      }}
                    >
                      Remove
                    </button>
                  </div>
                </div>

                <div style={{ fontSize: '13px', fontWeight: 'bold', color: '#3d2b1f', flexShrink: 0, whiteSpace: 'nowrap' }}>
                  {formatCurrency(item.lineTotal)}
                </div>
              </div>
            ))}
                </div>
              ));
            })()}
          </div>

          {/* BXGY offers unlocked by the cart but not applied (yet) — auto-applied when Y is already in
              the cart; otherwise add the offer item from here (the code is then applied automatically). */}
          <BxgyUnlockedCards offers={bxgyOffers} autoErrors={autoErrors} dismissed={dismissed}
            onAdd={(o) => setAddYFor({ code: o.code, needQty: o.needQty })} onApply={applyOfferByHand} />

          {/* Discount Code */}
          <div style={{ marginBottom: '16px' }}>
            {cart.discounts.length > 0 ? (
              <div>
                {cart.discounts.map((discount, index) => (
                  <div key={index} style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '8px 12px',
                    background: '#dbeafe',
                    borderRadius: '10px',
                    marginBottom: '8px'
                  }}>
                    <div>
                      <div style={{ fontSize: '13px', fontWeight: '600', color: '#1e40af' }}>
                        🎟️ {discount.code}
                      </div>
                      <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '2px' }}>
                        Applied at checkout
                      </div>
                      {/* BXGY: "add Y" / "add more" guidance while the offer isn't fully used */}
                      {discount.level === 'bxgy' && discount.bxgyHint && (
                        <div style={{ fontSize: '11px', color: '#b45309', fontWeight: '600', marginTop: '2px' }}>🎁 {discount.bxgyHint}</div>
                      )}
                      {discount.level === 'bxgy' && discount.bxgyWarning && (
                        <div style={{ fontSize: '11px', color: '#b45309', marginTop: '2px' }}>⚠️ {discount.bxgyWarning}</div>
                      )}
                      {discount.level === 'bxgy' && offerFor(discount.code) && addYButton(offerFor(discount.code))}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <button
                        onClick={() => removeDiscount(discount.code)}
                        style={{
                          padding: '4px 8px',
                          background: 'transparent',
                          color: '#1e40af',
                          border: 'none',
                          fontSize: '12px',
                          cursor: 'pointer'
                        }}
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : null}
            {discountToast && (
              <div style={{
                marginTop: '6px', padding: '6px 10px', borderRadius: '10px', fontSize: '12px', fontWeight: '600',
                background: discountToast.type === 'error' ? '#fef2f2' : '#f0fdf4',
                color: discountToast.type === 'error' ? '#dc2626' : '#16a34a',
                border: `1px solid ${discountToast.type === 'error' ? '#fecaca' : '#bbf7d0'}`
              }}>
                {discountToast.type === 'error' ? '⚠️' : '✅'} {discountToast.message}
              </div>
            )}
          </div>

          {/* Totals */}
          <div style={{ borderTop: '2px solid #e5e7eb', paddingTop: '16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
              <span style={{ fontSize: '14px', color: '#6b7280' }}>Subtotal</span>
              <span style={{ fontSize: '14px', fontWeight: '600', color: '#111827' }}>
                {formatCurrency(cart.subtotal)}
              </span>
            </div>

            {/* Show discount breakdown */}
            {cart.discounts.length > 0 && (
              <div style={{ marginBottom: '8px' }}>
                {cart.discounts.map((d, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                    <span style={{ fontSize: '14px', color: '#c88346', fontWeight: '600' }}>
                      🎟️ {d.code} {d.valueType === 'percentage' ? `(${d.value}%)` : ''}
                    </span>
                    <span style={{ fontSize: '14px', fontWeight: '600', color: '#16a34a' }}>
                      -{formatCurrency(d.amount || 0)}
                    </span>
                  </div>
                ))}
              </div>
            )}

            {/* Show gift card deduction */}
            {cart.giftCards.length > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ fontSize: '14px', color: '#27ae60', fontWeight: '600' }}>
                  Gift Card
                </span>
                <span style={{ fontSize: '14px', fontWeight: '600', color: '#27ae60' }}>
                  Applied
                </span>
              </div>
            )}

            {/* Round off */}
            {cart.roundOff !== undefined && cart.roundOff !== 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ fontSize: '13px', color: '#6b7280' }}>Round Off</span>
                <span style={{ fontSize: '13px', color: '#6b7280' }}>
                  {cart.roundOff > 0 ? '+' : ''}{formatCurrency(cart.roundOff)}
                </span>
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px', paddingTop: '8px', borderTop: '1px solid rgba(200, 131, 70, 0.12)' }}>
              <span style={{ fontSize: '18px', fontWeight: 'bold', color: '#3d2b1f' }}>Total</span>
              <span style={{ fontSize: '18px', fontWeight: 'bold', color: '#3d2b1f' }}>
                {formatCurrency(cart.total)}
              </span>
            </div>
          </div>
        </>
      )}
      {addYFor && <BxgyAddYModal code={addYFor.code} needQty={addYFor.needQty} onClose={() => setAddYFor(null)} />}
      <BxgyWarningPopup warning={warning} onClose={() => setWarning(null)} />
    </div>
  );
}

export default Cart;
